import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import LeadsPageEnterprise from "@/pages/LeadsPageEnterprise";
import CustomersPage from "@/pages/CustomersPage";
import ProductsPage from "@/pages/ProductsPage";
import WhatsAppCenter from "@/pages/WhatsAppCenter";
import AppointmentsPage from "@/pages/AppointmentsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import CRMReportsPage from "@/pages/CRMReportsPage";
import CampsPage from "@/pages/CampsPage";
import PrivyrImportPage from "@/pages/PrivyrImportPage";
import UserManagementPage from "@/pages/UserManagementPage";
import SettingsPage from "@/pages/SettingsPage";
import TasksPage from "@/pages/TasksPage";
import MessagesPage from "@/pages/MessagesPage";
import FollowUpsPage from "@/pages/FollowUpsPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import AIFollowupPage from "@/pages/AIFollowupPage";
import AIAutomationDashboard from "@/pages/AIAutomationDashboard";
import HuskyVoiceDocsPage from "@/pages/HuskyVoiceDocsPage";
import QueueManagementConsole from "@/pages/QueueManagementConsole";
import AIAgentDashboard from "@/pages/AIAgentDashboard";
import EligibleLeadsDashboard from "@/pages/EligibleLeadsDashboard";
import WhatsAppMonitorPage from "@/pages/WhatsAppMonitorPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Setup axios interceptor to include session token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['X-Session-Token'] = token;
  }
  return config;
});

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Clear localStorage token if auth fails
      localStorage.removeItem('session_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, setUser, loading, checkAuth, logout };
};

// Auth Callback Component - Processes session_id from Emergent Auth
const AuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then(response => {
          setUser(response.data);
          navigate('/dashboard', { replace: true, state: { user: response.data } });
        })
        .catch(error => {
          console.error("Auth error:", error);
          navigate('/', { replace: true });
        });
    } else {
      navigate('/', { replace: true });
    }
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center gradient-hero">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, user, loading }) => {
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// App Router Component
const AppRouter = ({ user, setUser, loading, logout }) => {
  const location = useLocation();

  // Check for session_id in URL hash synchronously - prevents race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage user={user} setUser={setUser} />} />
      <Route path="/dashboard" element={
        <ProtectedRoute user={user} loading={loading}>
          <Dashboard user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/leads" element={
        <ProtectedRoute user={user} loading={loading}>
          <LeadsPageEnterprise user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute user={user} loading={loading}>
          <CustomersPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/products" element={
        <ProtectedRoute user={user} loading={loading}>
          <ProductsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/whatsapp" element={
        <ProtectedRoute user={user} loading={loading}>
          <WhatsAppCenter user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/appointments" element={
        <ProtectedRoute user={user} loading={loading}>
          <AppointmentsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute user={user} loading={loading}>
          <AnalyticsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/crm-reports" element={
        <ProtectedRoute user={user} loading={loading}>
          <CRMReportsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/camps" element={
        <ProtectedRoute user={user} loading={loading}>
          <CampsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/import" element={
        <ProtectedRoute user={user} loading={loading}>
          <PrivyrImportPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute user={user} loading={loading}>
          <UserManagementPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/tasks" element={
        <ProtectedRoute user={user} loading={loading}>
          <TasksPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute user={user} loading={loading}>
          <MessagesPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute user={user} loading={loading}>
          <SettingsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/follow-ups" element={
        <ProtectedRoute user={user} loading={loading}>
          <FollowUpsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/integrations" element={
        <ProtectedRoute user={user} loading={loading}>
          <IntegrationsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/ai-followup" element={
        <ProtectedRoute user={user} loading={loading}>
          <AIFollowupPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/ai-automation" element={
        <ProtectedRoute user={user} loading={loading}>
          <AIAutomationDashboard user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/huskyvoice-docs" element={
        <ProtectedRoute user={user} loading={loading}>
          <HuskyVoiceDocsPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/queue-management" element={
        <ProtectedRoute user={user} loading={loading}>
          <QueueManagementConsole user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/ai-agent" element={
        <ProtectedRoute user={user} loading={loading}>
          <AIAgentDashboard user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/eligible-leads" element={
        <ProtectedRoute user={user} loading={loading}>
          <EligibleLeadsDashboard user={user} logout={logout} />
        </ProtectedRoute>
      } />
      <Route path="/whatsapp-monitor" element={
        <ProtectedRoute user={user} loading={loading}>
          <WhatsAppMonitorPage user={user} logout={logout} />
        </ProtectedRoute>
      } />
    </Routes>
  );
};

function App() {
  const { user, setUser, loading, logout } = useAuth();

  return (
    <BrowserRouter>
      <AppRouter user={user} setUser={setUser} loading={loading} logout={logout} />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
